<script setup>
import iconEssay from '@manseImages/nangman-naming/icon-essay.svg?url';
import iconNew from '@manseImages/nangman-naming/icon-new.svg?url';
import iconMessage from '@manseImages/nangman-naming/icon-message.svg?url';
import iconSearch from '@manseImages/nangman-naming/icon-search.svg?url';
import PopAnalyzeCriteria from './PopAnalyzeCriteria.vue';
import { getOhaeng } from '@/utils/ohaengPalette';
import { getNamingRulePalette } from '@/utils/namingRulePalette';
import avatarPerson from '@manseImages/nangman-naming/avatar-person.png';
import avatarPetAnimal from '@manseImages/nangman-naming/avatar-pet-animal.png';
import avatarPetPlant from '@manseImages/nangman-naming/avatar-pet-plant.png';
import avatarPetObject from '@manseImages/nangman-naming/avatar-pet-object.png';
import avatarBiz from '@manseImages/nangman-naming/avatar-biz.png';
import avatarCulture from '@manseImages/nangman-naming/avatar-culture.png';

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const props = defineProps({
  /** 대상 ('person' | 'pet' | 'biz' | 'culture') */
  target: { type: String, default: 'person' },
  /** 세부 옵션 */
  detail: { type: String, default: null },
  /** PopNamingFieldInput 결과 */
  detailInput: { type: Object, default: null },
  /** 작명 기준 사주 */
  saju: { type: Object, default: null },
  /** 분석기준보기 "내 사주" 천간지지 4기둥 (응답 사주정보.명식 변환). 비면 섹션 숨김 */
  sajuPillars: { type: Array, default: () => [] },
  /** 분석기준보기 "내 사주" 핵심 오행 태그(용신/희신/한신/기신/구신·조후·공망·격국) */
  sajuTags: { type: Array, default: () => [] },
  /**
   * 분석 결과 — 좌측 progress + 우측 카테고리 카드 동시에 사용.
   * TODO: [결과 API 연동] 부모(saju-naming/index.vue → DetailPageNaming)가
   *   modeKey/detailInput/guestUserId로 결과 API 호출 후 prop으로 주입. 아래 default mock 제거 예정.
   */
  analysis: {
    type: Object,
    default: () => ({
      score: 78,
      grade: 'B',
      ohaengFlow: ['금', '금', '수'],
      flowName: '인지상생',
      scoreItems: [
        {
          key: 'yongsin', label: '용신', value: 38, max: 40,
          headerBg: '#fdeeee', contentBg: 'rgba(253, 238, 238, 0.4)',
          gradeText: '최상', gradeColor: '#53b82c',
          title: '金+金+水 — 3자 전부 용신·한신 일치',
          desc: '사주의 약점을 보완하는 金 용신 오행이 2자 포함되어 부족한 기운을 채워요.',
        },
        {
          key: 'samwon', label: '삼원오행', value: 36, max: 40,
          headerBg: '#f2f8ef', contentBg: 'rgba(242, 248, 239, 0.4)',
          gradeText: '상', gradeColor: '#376ac9',
          title: '金·金·水 인지상생',
          desc: '【金金水 배치】 강직하고 기품 있어 신의를 지키고 성공운이 좋은 배치입니다.',
        },
        {
          key: 'johu', label: '조후', value: 9, max: 10,
          headerBg: '#eef3fb', contentBg: 'rgba(238, 243, 251, 0.4)',
          gradeText: '최상', gradeColor: '#53b82c',
          title: '필요오행 金+水 100% 부합',
          desc: '조후용신 金·水가 완벽히 반영되어 계절 기운의 균형이 잡힙니다.',
        },
        {
          key: 'ipmyo', label: '입묘', value: 6, max: 6,
          headerBg: '#fef7e8', contentBg: 'rgba(254, 247, 232, 0.4)',
          gradeText: '최상', gradeColor: '#53b82c',
          title: '위험 없음',
          desc: '오행이 입묘 관계에 놓이지 않아 기운이 자유롭게 발현됩니다.',
        },
        {
          key: 'gongmang', label: '공망', value: 2, max: 4,
          headerBg: '#f2f2f2', contentBg: 'rgba(242, 242, 242, 0.4)',
          gradeText: '중상', gradeColor: '#f3b713',
          title: '공망 오·미와 약한 충돌',
          desc: '공망 오·미(火·土)와 이름 金·水는 직접 충돌하지 않지만 경미한 영향이 있습니다.',
        },
      ],
      summary: [
        '종합적으로 양호한 이름이에요.',
        '용신 부합과 조후가 최상급이고 삼원 오행 배치도 강직함을 상징해요.',
      ],
      interpret: [
        '정재(김정재)는 사주 용신 金과 한신 水가 고루 담긴 이름이에요.',
        '金金水 배치는 강직하고 기품 있는 성품을 나타내며, 신의를 지키고 성공을 이루는 기운이 담겨있어요.',
      ],
      gameSummary: [
        '未月 乙木 일간, 신약에 조후가 급한 사주예요.',
        '金·水가 반드시 필요한 자리에 이름 金·金·水로 정확히 들어왔고, 인원→지원 金生水 상생까지 성립해요.',
        '조후 만점에 용신·삼원 모두 상급이에요.',
        '공망 未가 월지에 들어앉아 실현력에 약간의 흠이 있지만, 이름 자체로는 잘 짜인 구조예요.',
      ],
    }),
  },
});

const emit = defineEmits(['openCriteria']);

const modeKey = computed(() => `${props.target}-${props.detail}`);

/** 표시 이름 — 분석 대상은 사용자가 입력한 detailInput에서 구성.
 *  detailInput이 없는 옛 구매 건은 응답에서 추출한 analysis.name으로 폴백 (모바일과 동일) */
const targetName = computed(() => {
  const d = props.detailInput;
  if (d?.surname && d?.firstName) return `${d.surname}${d.firstName}`;
  if (d?.bizName) return d.bizName;
  if (d?.petName) return d.petName;
  if (d?.plantName) return d.plantName;
  if (d?.objectName) return d.objectName;
  if (d?.nickname) return d.nickname;
  if (d?.ahoHanja) return d.ahoHanja;
  return props.analysis?.name || '';
});

const targetSubText = computed(() => '');
const targetScore = computed(() => props.analysis?.score ?? 0);
const targetGrade = computed(() => props.analysis?.grade ?? '');

/** 모드 별 아바타 */
const MODE_AVATARS = {
  'person-kor': avatarPerson,
  'person-han': avatarPerson,
  'pet-animal': avatarPetAnimal,
  'pet-plant': avatarPetPlant,
  'pet-object': avatarPetObject,
  'biz-biz': avatarBiz,
  'culture-aho': avatarCulture,
  'culture-nickname': avatarCulture,
};
const modeAvatar = computed(() => MODE_AVATARS[modeKey.value] || avatarPerson);

const progressPercent = (item) => {
  if (!item.max) return 0;
  return Math.min(100, Math.round((item.value / item.max) * 100));
};

/** 등급 색 매핑 */
const GRADE_COLORS = {
  S: { color: '#53b82c', bg: 'rgba(83, 184, 44, 0.1)' },
  A: { color: '#53b82c', bg: 'rgba(83, 184, 44, 0.1)' },
  B: { color: '#376ac9', bg: 'rgba(55, 106, 201, 0.1)' },
  C: { color: '#e8ad41', bg: 'rgba(232, 173, 65, 0.1)' },
  D: { color: '#e65555', bg: 'rgba(230, 85, 85, 0.1)' },
};
const gradeStyle = computed(() => GRADE_COLORS[targetGrade.value] || GRADE_COLORS.B);

const showCriteriaPopup = ref(false);
const openCriteria = () => {
  showCriteriaPopup.value = true;
  emit('openCriteria');
};
</script>

<template>
  <VRow no-gutters class="full-height">
    <!-- 좌측 컬럼: 점수 요약 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col">
      <div class="naming-card-wrap right">
        <div class="naming-analyze">
          <!-- 헤더 -->
          <div class="naming-analyze__head">
            <div class="naming-analyze__head-row">
              <div class="naming-analyze__head-title">
                <img :src="iconSearch" alt="" width="24" height="24" />
                <span>분석 결과</span>
              </div>
              <button type="button" class="naming-analyze__criteria-btn" @click="openCriteria">
                분석 기준 보기
              </button>
            </div>
            <p class="naming-analyze__head-sub">입력하신 정보로 아래와 같이 분석하였습니다.</p>
          </div>

          <!-- 이름 카드 -->
          <div class="naming-analyze__name-card">
            <div class="naming-analyze__name-avatar">
              <img :src="modeAvatar" alt="" />
            </div>
            <div class="naming-analyze__name-body">
              <p class="naming-analyze__name-text">{{ targetName }}</p>
              <span v-if="targetSubText" class="naming-analyze__name-sub">{{ targetSubText }}</span>
            </div>
            <div class="naming-analyze__name-score">
              <span class="naming-analyze__name-score-num">{{ targetScore }}점</span>
              <span
                class="naming-analyze__name-grade"
                :style="{ background: gradeStyle.bg, color: gradeStyle.color }"
              >
                {{ targetGrade }}
              </span>
            </div>
          </div>

          <!-- 오행 변환 + progress -->
          <section class="naming-analyze__summary">
            <div class="naming-analyze__ohaeng-block">
              <div class="naming-analyze__ohaeng-row">
                <template v-for="(oh, i) in analysis.ohaengFlow" :key="`big-${oh}-${i}`">
                  <div
                    class="naming-analyze__ohaeng-box"
                    :style="{ background: getOhaeng(oh).solid }"
                  >
                    <span>{{ oh }}</span>
                  </div>
                  <span
                    v-if="i < analysis.ohaengFlow.length - 1"
                    class="naming-analyze__ohaeng-sep"
                  >·</span>
                </template>
              </div>
            </div>

            <div class="naming-analyze__score-list">
              <div
                v-for="item in analysis.scoreItems"
                :key="item.key"
                class="naming-analyze__score-row"
              >
                <span class="naming-analyze__score-label">{{ item.label }}</span>
                <div class="naming-analyze__score-bar">
                  <div
                    class="naming-analyze__score-bar-fill"
                    :style="{ width: `${progressPercent(item)}%`, background: getNamingRulePalette(item.label).color }"
                  />
                </div>
                <div class="naming-analyze__score-value">
                  <span class="naming-analyze__score-num">{{ item.value }}</span>
                  <span class="naming-analyze__score-max">/{{ item.max }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- 한 줄 정리 -->
          <section class="naming-analyze__panel naming-analyze__panel--summary">
            <header class="naming-analyze__panel-head">
              <img :src="iconNew" alt="" class="naming-analyze__panel-icon" />
              <span class="naming-analyze__panel-title">한 줄 정리</span>
            </header>
            <div class="naming-analyze__panel-body">
              <p v-for="(line, i) in analysis.summary" :key="`sum-${i}`">{{ line }}</p>
            </div>
          </section>

          <!-- 낭만 해석 -->
          <section class="naming-analyze__panel naming-analyze__panel--interpret">
            <header class="naming-analyze__panel-head">
              <img :src="iconEssay" alt="" class="naming-analyze__panel-icon" />
              <span class="naming-analyze__panel-title">낭만 해석</span>
            </header>
            <div class="naming-analyze__panel-body">
              <p v-for="(line, i) in analysis.interpret" :key="`int-${i}`">{{ line }}</p>
            </div>
          </section>
        </div>
      </div>
    </VCol>

    <!-- 우측 컬럼: 카테고리별 상세 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col">
      <div class="naming-card-wrap left">
        <div class="naming-analyze__right">
          <div class="naming-analyze__detail-list">
            <section
              v-for="item in analysis.scoreItems"
              :key="`detail-${item.key}`"
              class="naming-analyze__detail"
            >
              <header
                class="naming-analyze__detail-head"
                :style="{ background: item.headerBg }"
              >
                <span class="naming-analyze__detail-label">{{ item.label }}</span>
                <div class="naming-analyze__detail-meta">
                  <div class="naming-analyze__detail-score">
                    <span class="naming-analyze__detail-num">{{ item.value }}</span>
                    <span class="naming-analyze__detail-max">/{{ item.max }}</span>
                  </div>
                  <span
                    class="naming-analyze__detail-badge"
                    :style="{ color: item.gradeColor }"
                  >
                    {{ item.gradeText }}
                  </span>
                </div>
              </header>
              <div
                v-if="item.title || item.desc"
                class="naming-analyze__detail-body"
                :style="{ background: item.contentBg }"
              >
                <p v-if="item.title" class="naming-analyze__detail-title">{{ item.title }}</p>
                <p v-if="item.desc" class="naming-analyze__detail-desc">{{ item.desc }}</p>
              </div>
            </section>

            <!-- 명리총평 — gameSummary 데이터 있을 때만 표시 (미제공 시 숨김) -->
            <section v-if="analysis.gameSummary?.length" class="naming-analyze__game-summary">
              <header class="naming-analyze__game-head">
                <img :src="iconMessage" alt="" class="naming-analyze__game-icon" width="24" height="24" />
                <span class="naming-analyze__game-title">명리총평</span>
              </header>
              <div class="naming-analyze__game-body">
                <p v-for="(line, i) in analysis.gameSummary" :key="`game-${i}`">{{ line }}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </VCol>

    <PopAnalyzeCriteria
      :is-visible="showCriteriaPopup"
      :saju="{ pillars: sajuPillars, tags: sajuTags }"
      @close="showCriteriaPopup = false"
    />
  </VRow>
</template>

<style lang="scss" scoped>
$naming-accent: #714ce5;

/* === 공통: 좌우 컬럼 wrapper === */
.naming-card-wrap {
  height: 100%;
  background: #fff;
  overflow-y: auto;

  &.right {
    border-right: 1px solid #ebebeb;
  }

  &.left {
    border-left: 1px solid #ebebeb;
  }
}

.no-pad-col {
  padding: 0 !important;
}

.full-height {
  height: 100%;
}

/* === 좌측 컨테이너 === */
.naming-analyze {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 32px 40px 40px;
  background: #fff;
  box-sizing: border-box;
  width: 100%;
}

/* 헤더 */
.naming-analyze__head {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  box-sizing: border-box;
}

.naming-analyze__head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.naming-analyze__head-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  color: #333;
  line-height: 24px;
}

.naming-analyze__head-sub {
  margin: 0;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
}

.naming-analyze__criteria-btn {
  padding: 3px 10px;
  border: none;
  border-radius: 100px;
  background: $naming-accent;
  color: #fff;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
}

/* 이름 카드 */
.naming-analyze__name-card {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 93px;
  padding: 15px;
  border: 1.5px solid $naming-accent;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
}

.naming-analyze__name-avatar {
  width: 50px;
  height: 50px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

.naming-analyze__name-body {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.naming-analyze__name-text {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 1px;
}

.naming-analyze__name-sub {
  color: #575757;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: normal;
}

.naming-analyze__name-score {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.naming-analyze__name-score-num {
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.naming-analyze__name-grade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 3px 6px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 12px;
  box-sizing: border-box;
}

/* 오행 + progress 영역 */
.naming-analyze__summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 15px 0 20px;
  border-radius: 12px;
  width: 100%;
}

.naming-analyze__ohaeng-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.naming-analyze__ohaeng-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.naming-analyze__ohaeng-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: #fff;
  font-family: 'ChosunGs', serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.2;
}

.naming-analyze__ohaeng-sep {
  display: inline-flex;
  align-self: stretch;
  align-items: center;
  color: #575757;
  font-family: Pretendard;
  font-size: 28px;
  font-weight: 500;
  line-height: 1;
}

/* 점수 progress */
.naming-analyze__score-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
  width: 100%;
}

.naming-analyze__score-row {
  display: flex;
  align-items: center;
  gap: 15px;
}

.naming-analyze__score-label {
  flex-shrink: 0;
  width: 60px;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}

.naming-analyze__score-bar {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 10px;
  border-radius: 10px;
  background: #ebebeb;
  overflow: hidden;
}

.naming-analyze__score-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.naming-analyze__score-value {
  flex-shrink: 0;
  width: 50px;
  text-align: right;
  font-family: Pretendard;
  line-height: 1.3;
}

.naming-analyze__score-num {
  color: #333;
  font-size: 15px;
  font-weight: 500;
}

.naming-analyze__score-max {
  color: #757575;
  font-size: 15px;
  font-weight: 400;
}

/* 한 줄 정리 / 낭만 해석 카드 */
.naming-analyze__panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
}

.naming-analyze__panel-head {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 40px;
  padding: 8px 16px;
  box-sizing: border-box;
}

.naming-analyze__panel-title {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.naming-analyze__panel-icon {
  width: 24px;
  height: 24px;
}

.naming-analyze__panel-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 16px 24px;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;

  p {
    margin: 0;
  }
}

.naming-analyze__panel--summary {
  .naming-analyze__panel-head {
    background: #f0eef7;
  }
  .naming-analyze__panel-body {
    background: #f9f8fc;
  }
}

.naming-analyze__panel--interpret {
  .naming-analyze__panel-head {
    background: #f7f3ee;
  }
  .naming-analyze__panel-body {
    background: rgba(247, 243, 238, 0.4);
  }
}

/* === 우측 컨테이너 === */
.naming-analyze__right {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 40px;
  background: #fff;
  box-sizing: border-box;
  width: 100%;
}

.naming-analyze__detail-list {
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: stretch;
  width: 100%;
}

/* 카테고리 상세 카드 */
.naming-analyze__detail {
  display: flex;
  flex-direction: column;
}

.naming-analyze__detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 8px 24px;
  box-sizing: border-box;
}

.naming-analyze__detail-label {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.naming-analyze__detail-meta {
  display: flex;
  align-items: center;
  gap: 15px;
}

.naming-analyze__detail-score {
  display: inline-flex;
  align-items: baseline;
  font-family: Pretendard;
  line-height: 1.3;
}

.naming-analyze__detail-num {
  color: #333;
  font-size: 15px;
  font-weight: 500;
}

.naming-analyze__detail-max {
  color: #757575;
  font-size: 15px;
  font-weight: 400;
}

.naming-analyze__detail-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  padding: 5px 6px;
  border-radius: 4px;
  background: #fff;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 12px;
}

.naming-analyze__detail-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 16px 24px;

  p {
    margin: 0;
    color: #333;
    font-family: Pretendard;
    line-height: 24px;
  }
}

.naming-analyze__detail-title {
  font-size: 16px;
  font-weight: 500;
}

.naming-analyze__detail-desc {
  font-size: 15px;
  font-weight: 400;
}

/* 명리총평 */
.naming-analyze__game-summary {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.naming-analyze__game-head {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 40px;
  padding: 8px 16px;
  background: #fff4ed;
  box-sizing: border-box;
}

.naming-analyze__game-icon {
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.naming-analyze__game-title {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.naming-analyze__game-body {
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  background: rgba(255, 244, 237, 0.4);
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;

  p {
    margin: 0;
    line-height: 1.5;
  }
}
</style>
